import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, RotateCcw, CheckCircle2, Camera } from "lucide-react";
import { toast } from "sonner";
import { apiCreateReturn, apiUploadIfDataUrl } from "@/lib/api";
import { getCachedCustomer } from "@/lib/customerAuth";

const REASONS = [
  "Wrong size",
  "Damaged / defective",
  "Not as described",
  "Changed my mind",
  "Wrong item delivered",
  "Other",
];

// Lightweight, high-performance in-app return form. Reuses the existing
// /api/upload/image endpoint for photo hosting — no dedicated backend needed.
// Requires exactly 2 product photos per policy.
export const ReturnOrderDialog = ({ open, onClose, initialOrder = null, onSubmitted }) => {
  const [form, setForm] = useState({
    customer_name: "",
    order_id_or_phone: "",
    reason: REASONS[0],
    refund_details: "",
    notes: "",
    photo_urls: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDone(false);
      setForm({
        customer_name: "",
        order_id_or_phone: "",
        reason: REASONS[0],
        refund_details: "",
        notes: "",
        photo_urls: [],
      });
    } else if (initialOrder) {
      // Pre-fill when opened from the My Orders "Return Order" button.
      const cached = getCachedCustomer();
      setForm((p) => ({
        ...p,
        customer_name: initialOrder.name || cached?.name || p.customer_name,
        order_id_or_phone: initialOrder.id || initialOrder.phone || "",
        notes: initialOrder.productTitle
          ? `Order for: ${initialOrder.productTitle}`
          : p.notes,
      }));
    } else if (open) {
      const cached = getCachedCustomer();
      if (cached) {
        setForm((p) => ({
          ...p,
          customer_name: cached.name || p.customer_name,
          order_id_or_phone: cached.phone || p.order_id_or_phone,
        }));
      }
    }
  }, [open, initialOrder]);

  if (!open) return null;

  const update = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handlePhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const t = toast.loading(
      files.length === 1 ? "Uploading photo…" : `Uploading ${files.length} photos…`,
    );
    const uploaded = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      // Cap at 2 total (spec requires exactly 2).
      if (form.photo_urls.length + uploaded.length >= 2) break;
      try {
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = () => rej(r.error);
          r.readAsDataURL(file);
        });
        const url = await apiUploadIfDataUrl(dataUrl);
        uploaded.push(url);
      } catch {
        /* per-file failure; continue */
      }
    }
    setForm((p) => ({ ...p, photo_urls: [...p.photo_urls, ...uploaded].slice(0, 2) }));
    setUploading(false);
    if (uploaded.length > 0) {
      toast.success(
        uploaded.length === 1 ? "Photo attached" : `${uploaded.length} photos attached`,
        { id: t, duration: 1500 },
      );
    } else {
      toast.error("Upload failed", { id: t });
    }
  };

  const removePhoto = (idx) =>
    setForm((p) => ({ ...p, photo_urls: p.photo_urls.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.order_id_or_phone.trim() || !form.refund_details.trim()) {
      toast.error("Order ID / phone and refund UPI are required.");
      return;
    }
    if (!form.customer_name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (form.photo_urls.length !== 2) {
      toast.error("Please attach exactly 2 product photos.");
      return;
    }
    setSubmitting(true);
    const t = toast.loading("Filing your return request…");
    try {
      await apiCreateReturn(form);
      toast.success("Return request submitted", {
        id: t,
        duration: 2200,
        description: "Our team will contact you within 24 hours.",
      });
      setDone(true);
      // Let the parent (My Orders) know so it can refresh the return-status
      // pill on the source order card.
      if (typeof onSubmitted === "function") onSubmitted();
    } catch (err) {
      toast.error(err?.message || "Could not submit", { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="return-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
        data-testid="return-dialog"
      >
        <motion.div
          key="return-card"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_24px_64px_rgba(0,0,0,0.25)] max-h-[92vh] overflow-y-auto"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-white/95 backdrop-blur border-b border-black/5">
            <div className="flex items-center gap-2">
              <RotateCcw size={16} strokeWidth={1.8} className="text-[#064E3B]" />
              <h2 className="font-display text-lg text-[#0B0C10]">Return an order</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="return-dialog-close"
              className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"
              aria-label="close"
            >
              <X size={18} />
            </button>
          </div>

          {done ? (
            <div className="p-8 text-center space-y-3" data-testid="return-success">
              <CheckCircle2 size={44} strokeWidth={1.5} className="text-[#064E3B] mx-auto" />
              <h3 className="font-display text-xl text-[#0B0C10]">Return request received</h3>
              <p className="text-sm text-neutral-600">
                We&apos;ll reach out on your phone within 24 hours with pickup details.
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="return-done-close"
                className="mt-3 inline-flex items-center gap-2 bg-[#0B0C10] text-white rounded-full px-6 py-2.5 text-sm font-medium"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-5 space-y-4">
              {/* Verbatim lowercase policy note — must be shown prominently
                  at the top of every return submission. */}
              <div
                data-testid="return-policy-note"
                className="rounded-2xl bg-[#FFF5D6] border border-[#E0B53C]/40 px-4 py-3 text-sm font-medium text-[#7A5A00] leading-relaxed"
              >
                Note: use product not accepted
              </div>

              <Field label="Your Name" required>
                <input
                  data-testid="return-name-input"
                  value={form.customer_name}
                  onChange={update("customer_name")}
                  placeholder="Aisha Kapoor"
                  className="w-full bg-white border border-black/15 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm"
                />
              </Field>

              <Field label="Order ID or Phone Number" required>
                <input
                  data-testid="return-order-input"
                  value={form.order_id_or_phone}
                  onChange={update("order_id_or_phone")}
                  placeholder="e.g. 9876543210 or order #abc-123"
                  className="w-full bg-white border border-black/15 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm"
                />
              </Field>

              <Field label="Reason for return" required>
                <select
                  data-testid="return-reason-select"
                  value={form.reason}
                  onChange={update("reason")}
                  className="w-full bg-white border border-black/15 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm"
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={`Product photos — required (${form.photo_urls.length}/2)`} required>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhoto}
                  data-testid="return-photo-input"
                  className="hidden"
                />
                <div className="grid grid-cols-2 gap-3">
                  {[0, 1].map((slot) => {
                    const url = form.photo_urls[slot];
                    return (
                      <div
                        key={slot}
                        data-testid={`return-photo-slot-${slot}`}
                        className="relative aspect-square rounded-xl border border-dashed border-black/20 bg-neutral-50 overflow-hidden"
                      >
                        {url ? (
                          <>
                            <img
                              src={url}
                              alt={`photo ${slot + 1}`}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(slot)}
                              data-testid={`return-photo-remove-${slot}`}
                              aria-label="remove photo"
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 flex items-center justify-center shadow"
                            >
                              <X size={12} />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid={`return-photo-add-${slot}`}
                            className="w-full h-full flex flex-col items-center justify-center gap-1 text-neutral-500 text-[11px] font-medium hover:bg-neutral-100"
                          >
                            {uploading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Camera size={18} strokeWidth={1.6} />
                            )}
                            Photo {slot + 1}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-neutral-500 mt-1.5">
                  Two photos are mandatory — front &amp; back / packaging &amp; product help us verify quickly.
                </p>
              </Field>

              <Field label="Refund UPI ID / Bank details" required>
                <textarea
                  data-testid="return-refund-input"
                  value={form.refund_details}
                  onChange={update("refund_details")}
                  placeholder="UPI: yourname@upi  •  or  •  A/C 1234…, IFSC ABCD0…, Bank Name"
                  rows={2}
                  className="w-full bg-white border border-black/15 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm resize-none"
                />
              </Field>

              <Field label="Additional notes (optional)">
                <textarea
                  data-testid="return-notes-input"
                  value={form.notes}
                  onChange={update("notes")}
                  rows={2}
                  className="w-full bg-white border border-black/15 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-3 py-2.5 text-sm resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid="return-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3 text-sm font-medium disabled:opacity-60 hover:-translate-y-0.5 transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Submit return request"
                )}
              </button>
              <p className="text-[10px] text-neutral-500 text-center">
                Refunds are processed within 5–7 working days of pickup.
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5">
      {label} {required && <span className="text-[#E0B53C]">*</span>}
    </span>
    {children}
  </label>
);
